import clsx from 'clsx';
import { formatEther, formatUnits } from 'ethers/lib/utils';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

import { ConnectWalletModal } from '../../../components/common/modal';
import { RewardsPool } from '../../../components/starsStaking/RewardsPool.component';
import { StakingPool } from '../../../components/starsStaking/StakingPool.component';
import { CoinGeckoContext } from '../../../contexts/app-state-context/coingecko/coingecko.context';
import { ModalStateContext } from '../../../contexts/app-state-context/modal-state.context';
import { useWeb3State } from '../../../hooks';
import { useStarsEthLpTokenContract } from '../../../hooks/lp-token-contract.hooks';
import { useStarStakingContract } from '../../../hooks/stars-staking.hook';
import type { StarsStaking } from '../../../lib/contracts/StarsStaking';
import styles from './StarsStakingPage.module.css';

export interface PoolInfo {
  contractPoolInfo: StarsStaking.PoolInfoStructOutput;
  apr: number;
  bnbToUSDRatio: number;
  starsToUSDRatio: number;
}

export const StarsStakingPage = () => {
  const { walletDetails } = useWeb3State();
  const [os, setOs] = useState('WindowsLike');
  const [pools, setPools] = useState<PoolInfo[]>([]);
  const [intervalStarted, setIntervalStarted] = useState(false);
  const { setModal } = useContext(ModalStateContext);
  const [numOfDeposits, setNumOfDeposits] = useState<number[]>([]);
  const [numOfDepositsShow, setNumOfDepositsShow] = useState(0);
  const [selected, setSelected] = useState(true);
  const { starsUsdRate: starsToUSDRatio, bnbUsdRate: bnbToUSDRatio } =
    useContext(CoinGeckoContext);

  const {
    getDeposit,
    getPool,
    getPoolLength,
    depositLength,
    contractInitialized,
    pendingSTARS,
  } = useStarStakingContract();
  const { getStarsEthTotalSupply, getStarsEthReserves, initialized } =
    useStarsEthLpTokenContract();

  useEffect(() => {
    const isMac =
      navigator.userAgent.includes('Mac') ||
      navigator.userAgent.includes('like Mac');

    if (!isMac) {
      return;
    }

    setOs('MacintoshLike');
  }, []);

  useEffect(() => {
    const getPools = async () => {
      if (!walletDetails.address) {
        return;
      }

      const newPools: PoolInfo[] = [];
      const length = await getPoolLength();
      for (let x = 0; x < length.poolLength; x++) {
        const pool = (await getPool(x)).pool;
        if (pool !== undefined) {
          let apr = 0;
          if (x === 4) {
            // TODO: change in production
            const starsBnbTotalSupply = parseFloat(
              formatEther(await getStarsEthTotalSupply()),
            );
            const currentBnbStarsReserves = await getStarsEthReserves();
            const bnbReserves = parseFloat(
              formatEther(currentBnbStarsReserves[0]),
            );
            const starsReserves = parseFloat(
              formatEther(currentBnbStarsReserves[1]),
            );
            const LP_RESERVE_USD_VALUE =
              starsReserves * starsToUSDRatio + bnbReserves * bnbToUSDRatio;
            const starsUSDPerDay =
              28660 * parseFloat(formatUnits(pool[6], 18)) * starsToUSDRatio;
            const lpUSD =
              (parseFloat(formatUnits(pool[7], 18)) * LP_RESERVE_USD_VALUE) /
              starsBnbTotalSupply;
            const daily = starsUSDPerDay / lpUSD;
            apr =
              365 * daily * 100 === Infinity || Number.isNaN(daily)
                ? 0
                : 365 * daily * 100;
          } else {
            const starsPerDay = 28660 * parseFloat(formatUnits(pool[6], 18)); // mogulPerSecond
            const daily = starsPerDay / parseFloat(formatUnits(pool[7], 18)); // totalDepositedLP
            apr = 365 * daily * 100 === Infinity ? 0 : 365 * daily * 100;
          }

          newPools.push({
            contractPoolInfo: pool,
            apr,
            starsToUSDRatio, // TODO: change in production
            bnbToUSDRatio,
          });
        }
      }
      setPools(newPools);
      const depositLengths: number[] = [];
      let depositsAmounts = 0;
      for (let x = 0; x < length.poolLength; x++) {
        const lengthVal = await depositLength(x, walletDetails.address);
        depositLengths.push(lengthVal.length ? lengthVal.length.toNumber() : 0);
        for (let y = 0; y < depositLengths[x]; y++) {
          const deposit = await getDeposit(x, walletDetails.address, y);
          const rewardInfo = await pendingSTARS(x, walletDetails.address, y);
          const deposited = +parseFloat(
            formatUnits(deposit.deposit?.amount ?? 0, 18),
          );
          const rewards = +parseFloat(
            formatUnits(rewardInfo.pendingStars ?? 0, 18),
          );
          const totalAmount = deposited + rewards;

          depositsAmounts += totalAmount;
        }
      }
      setNumOfDeposits(depositLengths);
      setNumOfDepositsShow(depositsAmounts);
    };

    if (contractInitialized && initialized) {
      setIntervalStarted(true);
      void getPools();

      // Fetch every five seconds
      if (!intervalStarted) {
        const interval = setInterval(() => void getPools(), 5000);

        return () => clearInterval(interval);
      }
    }
  }, [contractInitialized, initialized, walletDetails.address]);

  const toggle = () => {
    setSelected(!selected);
  };

  const getPoolLocalInfo = (index: number) => {
    if (index === 0) {
      return {
        name: 'VIP Pool',
        description: [
          'Token: MOGUL',
          '# of Rewards: 1,000,000 MOGUL',
          'Min. Lockup: 1 month',
          'Reward Payment: 3 months',
        ],
        duration: 3,
        lockup: 1,
      };
    } else if (index === 1) {
      return {
        name: 'Backstage Pool',
        description: [
          'Token: MOGUL',
          '# of Rewards: 3,000,000 MOGUL',
          'Min. Lockup: 2 months',
          'Reward Payment: 6 months',
        ],
        duration: 6,
        lockup: 2,
      };
    } else if (index === 3) {
      return {
        name: 'Red Carpet Pool',
        description: [
          'Token: MOGUL',
          '# of Rewards: 6,000,000 MOGUL',
          'Min. Lockup: 3 months',
          'Reward Payment: 12 months',
        ],
        duration: 12,
        lockup: 3,
      };
    } else {
      return {
        name: 'Unknown Pool',
        description: ['Unknown Pool'],
        duration: 0,
        lockup: 0,
      };
    }
  };

  const baseToggleCn = `lg:w-56 md:w-40 w-32 px-3 flex justify-center items-center md:text-4xl sm:text-3xl text-2xl pointer ${
    os === 'WindowsLike' ? 'pb-2 pt-3' : 'pb-3 pt-2'
  }`;

  return (
    <div
      className={styles.stakingContainer + ' flex flex-col items-center py-16'}
    >
      <div className={styles.headersContainer + ' box-border'}>
        <div className="flex">
          <h1 className={`${styles.headers} p-0 m-0 relative`}>Staking</h1>
        </div>
        <div className="flex justify-center mb-10 mt-4">
          <div
            className={clsx(
              baseToggleCn,
              selected ? styles.toggleLeftSelected : styles.toggleLeft,
            )}
            onClick={toggle}
            onKeyPress={toggle}
            tabIndex={0}
            role="button"
          >
            Mogul Pools
          </div>
          <div
            className={clsx(
              baseToggleCn,
              selected ? styles.toggleRight : styles.toggleRightSelected,
            )}
            onClick={toggle}
            tabIndex={0}
            onKeyPress={toggle}
            role="button"
          >
            Reward
          </div>
        </div>
        <div className={styles.subHeader + ' text-xl text-center'}>
          New Mogul pools are coming soon. Stay tuned!
        </div>
      </div>
      {selected ? (
        <div
          className={
            styles.poolContainer +
            ' mt-10 flex justify-between flex-wrap gap-8 box-border'
          }
        >
          {pools.map((pool, i) => {
            return (
              <StakingPool
                poolId={i}
                duration={getPoolLocalInfo(i).duration}
                lockup={getPoolLocalInfo(i).lockup}
                poolName={getPoolLocalInfo(i).name}
                poolDescription={getPoolLocalInfo(i).description}
                apr={pool.apr.toFixed(2).toString()}
                totalDepositedLP={parseFloat(
                  formatUnits(pool.contractPoolInfo[7], 18), // totalDepositedLP
                )}
                starsPerBlock={parseFloat(
                  formatUnits(pool.contractPoolInfo[6], 18), // mogulPerSecond
                )}
                minLockUpBlocks={pool.contractPoolInfo[3].toNumber()} // minLockupTime
                startBlock={pool.contractPoolInfo[4].toNumber()} // startTime
                endBlock={pool.contractPoolInfo[5].toNumber()} // endTime
                starsToUSDRatio={pool.starsToUSDRatio}
                bnbToUSDRatio={pool.bnbToUSDRatio}
                // blockNumber={blockNumber}
                blockNumber={moment().utcOffset(0).unix()}
                key={i}
              />
            );
          })}
        </div>
      ) : (
        <div className={' mt-10 flex justify-center flex-wrap box-border'}>
          {!walletDetails.address ? (
            <button
              type="button"
              className={styles.connectWalletButton}
              onClick={() => {
                try {
                  setModal(ConnectWalletModal());
                } catch (e) {
                  toast.error('User rejected wallet connect');
                }
              }}
            >
              Connect Wallet
            </button>
          ) : numOfDepositsShow === 0 ? (
            <div className={styles.subHeader}>
              STAKE YOUR MOGUL TO SEE REWARDS!
            </div>
          ) : (
            numOfDeposits.reduce((partialSum, a) => partialSum + a, 0) > 0 &&
            pools.map((pool, i) => (
              <RewardsPool
                depositLength={numOfDeposits[i]}
                poolName={getPoolLocalInfo(i).name}
                apr={pool.apr.toFixed(2).toString()}
                key={i}
                index={i}
                endBlock={pool.contractPoolInfo[5].toNumber()}
                starsPerBlock={parseFloat(
                  formatUnits(pool.contractPoolInfo[6], 18),
                )}
                totalDepositedLP={parseFloat(
                  formatUnits(pool.contractPoolInfo[7], 18),
                )}
                // blockNumber={blockNumber}
                blockNumber={moment().utcOffset(0).unix()}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};
